"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import { voiceInput } from "@/lib/api";

interface Props {
  onSend: (text: string) => Promise<void>;
  isLoading: boolean;
  placeholder?: string;
}

export default function ChatInput({ onSend, isLoading, placeholder }: Props) {
  const [text, setText] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  // Auto-focus on mount
  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  // Auto-resize textarea
  useEffect(() => {
    const el = textareaRef.current;
    if (el) {
      el.style.height = "auto";
      el.style.height = Math.min(el.scrollHeight, 140) + "px";
    }
  }, [text]);

  const handleSubmit = async () => {
    const trimmed = text.trim();
    if (!trimmed || isLoading) return;
    setText("");
    await onSend(trimmed);
    textareaRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : "audio/webm";
      const recorder = new MediaRecorder(stream, { mimeType });
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mimeType });
        if (blob.size === 0) return;

        setIsTranscribing(true);
        try {
          const result = await voiceInput(blob, "recording.webm");
          if (result.transcript) {
            await onSend(result.transcript);
          }
        } catch (err) {
          console.error("Voice transcription failed:", err);
        } finally {
          setIsTranscribing(false);
        }
      };

      mediaRecorderRef.current = recorder;
      recorder.start();
      setIsRecording(true);
    } catch {
      // User denied mic or not supported
    }
  }, [onSend]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
  }, []);

  const handleVoiceClick = useCallback(() => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  }, [isRecording, startRecording, stopRecording]);

  const busy = isLoading || isTranscribing;

  return (
    <div className="border-t border-border bg-bg/80 backdrop-blur-sm px-4 py-3">
      <div className="max-w-2xl mx-auto flex items-end gap-2">
        {/* Voice button */}
        <button
          onClick={handleVoiceClick}
          disabled={busy}
          className={`flex-shrink-0 w-10 h-10 rounded-xl
                     flex items-center justify-center transition-all duration-200
                     ${isRecording
                       ? "bg-rose text-white animate-pulse"
                       : "text-muted hover:text-forest hover:bg-secondary/50"
                     }
                     disabled:opacity-30 disabled:cursor-not-allowed`}
          aria-label={isRecording ? "Stop recording" : "Voice input"}
        >
          {isRecording ? (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <rect x="6" y="6" width="12" height="12" rx="2" />
            </svg>
          ) : (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
              <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
              <line x1="12" y1="19" x2="12" y2="23" />
              <line x1="8" y1="23" x2="16" y2="23" />
            </svg>
          )}
        </button>
        <textarea
          ref={textareaRef}
          value={isRecording ? "Recording..." : isTranscribing ? "Transcribing..." : text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={
            placeholder ||
            "Tell me what's on your mind — a task, a worry, an idea..."
          }
          // Stable accessible name independent of the dynamic
          // placeholder so screen readers keep saying "Chat message"
          // even while transcribing or recording.
          aria-label="Chat message"
          className={`flex-1 resize-none border rounded-xl px-4 py-2.5
                     outline-none focus:border-forest/40 focus:ring-1 focus:ring-forest/20
                     bg-card text-text
                     placeholder:text-muted text-[14px]
                     leading-relaxed min-h-[42px] transition-all
                     ${isRecording ? "border-rose" : "border-border"}`}
          rows={1}
          disabled={busy || isRecording}
        />
        <button
          onClick={handleSubmit}
          disabled={!text.trim() || busy || isRecording}
          className="flex-shrink-0 w-10 h-10 rounded-xl
                     bg-forest text-white hover:bg-forest-med
                     disabled:opacity-30 disabled:cursor-not-allowed
                     transition-all duration-200 flex items-center justify-center"
        >
          {busy ? (
            <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="22" y1="2" x2="11" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
          )}
        </button>
      </div>
      <p className="text-[10px] text-muted text-center mt-1.5">
        Enter to send · Shift+Enter for new line{isRecording ? " · Tap mic to stop" : ""}
      </p>
    </div>
  );
}
